import { Component, OnInit, ChangeDetectionStrategy, inject, ChangeDetectorRef, Input, Output, EventEmitter } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ToolSummary, AgentToolDto, TransferRuleDto, TransferMode, AgentSummary, TelecomAccountListItemResult } from '@generated/models';
import { systemToolTitle, systemToolDescription } from '@shared/utils/system-tool-i18n';
import { AgentsService } from '@generated/services/agents.service';
import { TelecomService } from '@generated/services/telecom.service';
import { NotificationService } from '@core/services/notification.service';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CardModule } from 'primeng/card';
import { ToolbarModule } from 'primeng/toolbar';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantAnchorDirective } from '@shared/directives/assistant-anchor.directive';
import { SYSTEM_TOOL_FIELDS } from '@core/assistant/assistant-catalog';

@Component({
  selector: 'app-system-tool',
  imports: [
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantAnchorDirective,
    FormsModule,
    RouterLink,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    ToggleSwitchModule,
    ProgressSpinnerModule,
    CardModule,
    ToolbarModule
],
  templateUrl: './system-tool.html',
  styleUrl: './system-tool.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SystemToolComponent implements OnInit {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = SYSTEM_TOOL_FIELDS;

  @Input() tool!: ToolSummary;
  @Input() agentTool?: AgentToolDto;
  @Input() isEmbedded: boolean = false;
  @Input() availableAgents: AgentSummary[] = [];

  /**
   * Номер, с которого звоним оператору вне телефонного разговора. Живёт на
   * АГЕНТЕ, а не на инструменте: телеком-аккаунт один на агента, и держать его
   * копию в каждом правиле значило бы чинить рассинхрон руками. Показываем
   * здесь, потому что смысл у поля ровно один — этот инструмент.
   */
  @Input() defaultPhoneNumberAgentId: string | null = null;

  @Output() toolSaved = new EventEmitter<AgentToolDto>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() defaultPhoneNumberAgentIdChange = new EventEmitter<string | null>();

  private agentsService = inject(AgentsService);
  private telecomService = inject(TelecomService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  /** Подключённые телеком-аккаунты с номерами — источник выпадашки. */
  protected phoneNumberGroups: {
    label: string;
    items: { label: string; value: string }[];
  }[] = [];

  protected phoneNumbersLoading = false;

  ngOnInit(): void {
    // Component initialization - no form setup needed as we work directly with agentTool
    if (this.isOperatorTool()) {
      this.loadPhoneNumbers();
    }
  }

  /**
   * Номера грузим одним запросом за аккаунтами: ручка уже отдаёт их вместе, и
   * отдельного эндпоинта под выпадашку заводить не за чем.
   *
   * Аккаунты без номеров пропускаем — пустая группа в списке выглядит как
   * поломка. Ошибку не показываем всплывашкой: инструмент настраивается и без
   * номера, а без списка человек увидит пустую выпадашку и поймёт сам.
   */
  private loadPhoneNumbers(): void {
    this.phoneNumbersLoading = true;
    this.telecomService.apiTelecomAccountsGet().subscribe({
      next: accounts => {
        this.phoneNumberGroups = (accounts || [])
          .map((account: TelecomAccountListItemResult) => ({
            label: account.account_identifier || account.provider || '—',
            items: (account.phone_number_agents || [])
              .filter(n => !!n.id)
              .map(n => ({
                // Метка номера помогает, когда их несколько: «+1302… — Support».
                label: n.label ? `${n.phone_number} — ${n.label}` : (n.phone_number || ''),
                value: n.id!,
              })),
          }))
          .filter(group => group.items.length > 0);
        this.phoneNumbersLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.phoneNumbersLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /** Пустая строка из селекта — это «не выбрано», в базе должен лежать null. */
  updateDefaultPhoneNumber(value: string | null): void {
    this.defaultPhoneNumberAgentId = value || null;
    this.defaultPhoneNumberAgentIdChange.emit(this.defaultPhoneNumberAgentId);
  }

  // Update methods for form fields
  updateDescription(value: string): void {
    if (this.agentTool) {
      this.agentTool.custom_description = value || null;
    }
  }

  updateDisableInterruptions(value: boolean): void {
    if (this.agentTool) {
      this.agentTool.custom_disable_interruptions = value;
    }
  }

  // Getter for transfer rules
  get transferRules(): TransferRuleDto[] {
    return this.agentTool?.transfer_rules || [];
  }

  /** Опции для селекта TransferMode — используется в UI transfer-to-operator.
   *   Cold        — тупо переводим звонок, оператор ничего не слышит от нас.
   *   WarmMessage — сначала на связи с оператором проигрываем transfer_message.
   *   WarmSummary — сначала на связи с оператором проигрываем авто-саммари беседы. */
  readonly transferModeOptions: { label: string; value: TransferMode }[] = [
    { label: $localize`:@@systemTool.transferMode.cold:Cold (no briefing)`, value: 'Cold' },
    { label: $localize`:@@systemTool.transferMode.warmMessage:Warm — custom message`, value: 'WarmMessage' },
    { label: $localize`:@@systemTool.transferMode.warmSummary:Warm — auto summary`, value: 'WarmSummary' },
  ];

  /**
   * Идентификатор якоря внутри поверхности: код инструмента + имя поля.
   *
   * Поверхность общая для всех инструментов (иначе скрипт покрытия перестаёт
   * видеть файл), а поля у каждого свои — значит различать их должен якорь.
   * Собранный id совпадает с тем, что регистрирует страница агента:
   * `systemTool.<code>.<field>`.
   */
  anchorFor(field: string): string {
    const code = this.tool?.code ?? '';
    return code ? `${code}.${field}` : '';
  }

  isOperatorTool(): boolean {
    return this.tool?.code === 'transfer-to-operator';
  }

  isAgentTool(): boolean {
    return this.tool?.code === 'transfer-to-agent';
  }

  /** SKN-349. Тул знаний: те же правила, но «условие → текст ответа». */
  isKnowledgeTool(): boolean {
    return this.tool?.code === 'get-knowledge';
  }

  // Transfer Rules methods
  addTransferRule(): void {
    if (!this.agentTool) return;

    // Defaults зависят от инструмента: у transfer-to-operator по-умолчанию
    // Cold-transfer + пустой transfer_to_phone_number; у transfer-to-agent
    // transfer_mode не используется (агенту к агенту нечего «прогревать»).
    const isOperator = this.isOperatorTool();
    // У правила знаний ни цели перевода, ни задержки, ни режима нет —
    // оставляем их пустыми, чтобы бэк не сохранил мусор в неиспользуемые
    // колонки общей таблицы.
    const newRule: TransferRuleDto = {
      id: crypto.randomUUID(),
      transfer_to_agent_id: null,
      condition: '',
      delay_before_transfer_ms: 0,
      transfer_message: '',
      operator_briefing: null,
      enable_first_message: false,
      transfer_mode: isOperator ? 'Cold' : undefined,
      transfer_to_phone_number: null,
      ringing_timeout_seconds: isOperator ? 60 : undefined,
      answer: this.isKnowledgeTool() ? '' : null,
    };

    if (!this.agentTool.transfer_rules) {
      this.agentTool.transfer_rules = [];
    }
    this.agentTool.transfer_rules.push(newRule);
    this.cdr.markForCheck();
  }

  removeTransferRule(index: number): void {
    if (!this.agentTool?.transfer_rules) return;
    this.agentTool.transfer_rules.splice(index, 1);
    this.cdr.markForCheck();
  }

  /** Правило знаний: тема (по ней модель выбирает) + непустой текст ответа. */
  isKnowledgeRuleValid(rule: TransferRuleDto): boolean {
    return !!(rule.condition?.trim() && rule.answer?.trim());
  }

  // Check if transfer rule is valid
  isTransferRuleValid(rule: TransferRuleDto): boolean {
    if (this.isOperatorTool()) {
      return !!(rule.transfer_mode && rule.transfer_to_phone_number?.trim() && rule.condition?.trim());
    }
    return !!(rule.transfer_to_agent_id && rule.condition?.trim());
  }

  // Save functionality
  save(): void {
    if (!this.agentTool) return;

    // Валидация transfer rules зависит от инструмента.
    if (this.isAgentTool() && this.agentTool.transfer_rules && this.agentTool.transfer_rules.length > 0) {
      const invalidRules = this.agentTool.transfer_rules.filter(rule =>
        !rule.transfer_to_agent_id || !rule.condition?.trim()
      );

      if (invalidRules.length > 0) {
        this.notificationService.showError($localize`:@@systemTool.agent.validation.required:Please fill in all required fields: Agent and Condition for each transfer rule`);
        return;
      }
    }

    if (this.isOperatorTool() && this.agentTool.transfer_rules && this.agentTool.transfer_rules.length > 0) {
      const invalidRules = this.agentTool.transfer_rules.filter(rule =>
        !rule.transfer_mode || !rule.transfer_to_phone_number?.trim() || !rule.condition?.trim()
      );

      if (invalidRules.length > 0) {
        this.notificationService.showError($localize`:@@systemTool.operator.validation.required:Please fill in all required fields: Transfer mode, Phone number and Condition for each transfer rule`);
        return;
      }
    }

    if (this.isKnowledgeTool() && this.agentTool.transfer_rules && this.agentTool.transfer_rules.length > 0) {
      const invalidRules = this.agentTool.transfer_rules.filter(rule => !this.isKnowledgeRuleValid(rule));

      if (invalidRules.length > 0) {
        this.notificationService.showError($localize`:@@systemTool.knowledge.validation.required:Please fill in Topic and Answer for every rule`);
        return;
      }
    }

    this.toolSaved.emit(this.agentTool);
  }

  cancel(): void {
    this.cancelled.emit();
  }

  /** Локализованный title инструмента (для readonly Name-инпута). */
  get localizedTitle(): string {
    return systemToolTitle(this.tool);
  }

  /**
   * Дефолтное описание инструмента — как оно пришло с бэка.
   *
   * Не локализуется намеренно: этот текст уезжает модели как промпт,
   * а не показывается человеку как подпись (SKN-360).
   */
  get defaultDescription(): string {
    return systemToolDescription(this.tool);
  }

  showDefaultDescription(): void {
    const def = this.defaultDescription;
    if (def && this.agentTool) {
      this.agentTool.custom_description = def;
      this.cdr.markForCheck();
    }
  }
}
